/** Small hooks the screens share: loading data, the customer's location, the clock. */
import {
  type DependencyList,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { errorMessage, get } from "./api";
import type { LatLng, Place } from "./types";

export type Resource<T> = {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  reload: () => Promise<void>;
  setData: (data: T) => void;
};

/** Loads data when the screen opens (and when `deps` change), with a way to reload. */
export function useResource<T>(load: () => Promise<T>, deps: DependencyList): Resource<T> {
  const [state, setState] = useState<{ data: T | undefined; error: unknown; loading: boolean }>({
    data: undefined,
    error: null,
    loading: true,
  });
  const latest = useRef(load);
  useLayoutEffect(() => {
    latest.current = load;
  });
  const generation = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++generation.current;
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await latest.current();
      if (mine === generation.current) setState({ data, error: null, loading: false });
    } catch (error) {
      if (mine === generation.current) setState((s) => ({ ...s, error, loading: false }));
    }
  }, []);

  useEffect(
    () => {
      void reload();
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: callers say when to reload.
    deps,
  );

  const setData = useCallback((data: T) => setState({ data, error: null, loading: false }), []);
  return { ...state, reload, setData };
}

/** Runs `tick` every `ms` while the screen is open and visible. */
export function useInterval(tick: () => void, ms: number | null) {
  const latest = useRef(tick);
  useLayoutEffect(() => {
    latest.current = tick;
  });
  useEffect(() => {
    if (ms === null) return;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") latest.current();
    }, ms);
    return () => clearInterval(timer);
  }, [ms]);
}

/** An element's height on screen as it changes, e.g. a sheet over a map, for the map to fit around. */
export function useHeight<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(() => setHeight(element.offsetHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, height];
}

/** The time now, updated every `ms`. */
export function useNow(ms = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useInterval(() => setNow(new Date()), ms);
  return now;
}

export type LocationState =
  | { status: "unknown" | "asking" | "denied" | "unavailable" }
  | { status: "known"; at: LatLng };

export function currentPosition(): Promise<LatLng> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("unavailable"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 },
    );
  });
}

/**
 * Where the customer is. Looked up straight away only if they've already allowed it;
 * otherwise `locate()` asks (call it from a tap).
 */
export function useMyLocation(): { location: LocationState; locate: () => Promise<LatLng | null> } {
  const [location, setLocation] = useState<LocationState>({ status: "unknown" });

  const locate = useCallback(async () => {
    setLocation({ status: "asking" });
    try {
      const at = await currentPosition();
      setLocation({ status: "known", at });
      return at;
    } catch (error) {
      const denied = (error as GeolocationPositionError).code === 1;
      setLocation({ status: denied ? "denied" : "unavailable" });
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    navigator.permissions
      ?.query({ name: "geolocation" })
      .then((permission) => {
        if (!cancelled && permission.state === "granted") void locate();
        if (!cancelled && permission.state === "denied") setLocation({ status: "denied" });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [locate]);

  return { location, locate };
}

/** Address and postcode search as the customer types, near a point. */
export function usePlaceSearch(query: string, near: LatLng) {
  const [state, setState] = useState<{ results: Place[]; loading: boolean; error: string | null }>({
    results: [],
    loading: false,
    error: null,
  });
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setState({ results: [], loading: false, error: null });
      return;
    }
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true }));
    const timer = setTimeout(() => {
      get<{ data: Place[] }>(
        `/places/search?q=${encodeURIComponent(q)}&lat=${near.lat.toFixed(5)}&lng=${near.lng.toFixed(5)}`,
        { signal: controller.signal },
      )
        .then((r) => setState({ results: r.data, loading: false, error: null }))
        .catch((error) => {
          if ((error as Error).name === "AbortError") return;
          setState({ results: [], loading: false, error: errorMessage(error) });
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, near.lat, near.lng]);
  return state;
}
